/**
 * Prospect list with TanStack Table
 */

import { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
  flexRender,
} from '@tanstack/react-table';
import { Phone, MoreVertical, Pencil, Trash2, Calendar, CheckCircle2, X, PhoneOff, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProspects, useDeleteProspect, useUpdateProspect } from '@/lib/api/hooks/useProspects';
import { useCanWrite } from '@/lib/auth/AuthProvider';
import type { Prospect, ProspectStatus } from '@/types/prospect';

const STATUS_OPTIONS: { value: ProspectStatus; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'qualified', label: 'Qualified', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'attempted', label: 'Attempted', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-gray-100 text-gray-800' },
  { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-800' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-800' },
  { value: 'meeting_booked', label: 'Meeting Booked', color: 'bg-purple-100 text-purple-800' },
  { value: 'proposal', label: 'Proposal', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-200 text-green-900' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-200 text-red-900' },
  { value: 'disqualified', label: 'Disqualified', color: 'bg-gray-200 text-gray-900' },
];

function getStatusColor(status: string): string {
  return STATUS_OPTIONS.find(s => s.value === status)?.color || 'bg-blue-100 text-blue-800';
}

interface ProspectListProps {
  onCallProspect?: (prospectId: string, phone: string, prospectName?: string) => void;
}

export function ProspectList({ onCallProspect }: ProspectListProps) {
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [queueFilter, setQueueFilter] = useState<'today' | 'future' | 'all'>('today');
  const pageSize = 20;
  const canWrite = useCanWrite();

  const { data, isLoading, error } = useProspects({
    limit: pageSize,
    offset: page * pageSize,
    queue: queueFilter,
  });

  const deleteProspect = useDeleteProspect();
  const updateProspect = useUpdateProspect();

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this prospect?')) return;
    try {
      await deleteProspect.mutateAsync(id);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleStatusChange = async (prospectId: string, newStatus: ProspectStatus) => {
    try {
      await updateProspect.mutateAsync({ id: prospectId, data: { status: newStatus } });
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const handleCall = (prospect: Prospect) => {
    const name = prospect.company_name || prospect.name || 'Unknown';
    onCallProspect?.(prospect.id, prospect.phone, name);
  };

  const getOutcomeBadge = (outcome?: string) => {
    if (!outcome) return null;

    const badges = {
      meeting_booked: { icon: <CheckCircle2 className="h-3 w-3" />, color: 'bg-green-100 text-green-800', label: 'Meeting' },
      callback: { icon: <Calendar className="h-3 w-3" />, color: 'bg-blue-100 text-blue-800', label: 'Callback' },
      not_interested: { icon: <X className="h-3 w-3" />, color: 'bg-red-100 text-red-800', label: 'Not Interested' },
      no_answer: { icon: <PhoneOff className="h-3 w-3" />, color: 'bg-gray-100 text-gray-800', label: 'No Answer' },
      voicemail: { icon: <MessageSquare className="h-3 w-3" />, color: 'bg-orange-100 text-orange-800', label: 'Voicemail' },
    };

    const badge = badges[outcome as keyof typeof badges];
    if (!badge) return null;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const columns: ColumnDef<Prospect>[] = [
    {
      accessorKey: 'company_name',
      header: 'Company',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">
            {row.original.company_name || row.original.name || '—'}
          </div>
          {row.original.unsubscribed && (
            <div className="mt-1">
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                title={row.original.unsubscribed_at ? `Opted out ${row.original.unsubscribed_at}` : 'Opted out of email communications'}
              >
                Opted out
              </span>
            </div>
          )}
          {row.original.last_call_outcome && (
            <div className="mt-1">
              {getOutcomeBadge(row.original.last_call_outcome)}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => canWrite ? (
        <Button
          variant="link"
          className="p-0 h-auto font-normal"
          onClick={() => handleCall(row.original)}
        >
          <Phone className="h-3 w-3 mr-1" />
          {row.original.phone}
        </Button>
      ) : (
        <span className="text-sm">{row.original.phone}</span>
      ),
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: ({ row }) => {
        const city = row.original.city;
        const state = row.original.state;
        if (!city && !state) return '—';
        return (
          <span>
            {city}
            {state && `, ${state}`}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status || 'new';
        if (!canWrite) {
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(status)}`}>
              {STATUS_OPTIONS.find(s => s.value === status)?.label || status}
            </span>
          );
        }
        return (
          <Select
            value={status}
            onValueChange={(value) => handleStatusChange(row.original.id, value as ProspectStatus)}
          >
            <SelectTrigger className={`h-7 w-[140px] text-xs font-medium border-0 ${getStatusColor(status)}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${opt.color}`}>
                    {opt.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: 'call_count',
      header: 'Calls',
      cell: ({ row }) => <span className="text-sm">{row.original.call_count || 0}</span>,
    },
    ...(canWrite ? [{
      id: 'actions',
      cell: ({ row }: { row: { original: Prospect } }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleCall(row.original)}>
              <Phone className="h-4 w-4 mr-2" />
              Call
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => handleDelete(row.original.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }] as ColumnDef<Prospect>[] : []),
  ];

  const table = useReactTable({
    data: data?.prospects || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (error) {
    return (
      <div className="text-center py-12 text-destructive">
        Error loading prospects: {error.message}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading prospects...
      </div>
    );
  }

  if (!data || data.prospects.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No prospects yet</p>
        <p className="text-sm mt-2">Upload a CSV file to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Queue Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium">Queue:</label>
        <Select value={queueFilter} onValueChange={(value) => setQueueFilter(value as 'today' | 'future' | 'all')}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today's Queue</SelectItem>
            <SelectItem value="future">Future Scheduled</SelectItem>
            <SelectItem value="all">All Prospects</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {queueFilter === 'today' && 'Showing prospects to call today'}
          {queueFilter === 'future' && 'Showing future scheduled callbacks'}
          {queueFilter === 'all' && 'Showing all prospects'}
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {page * pageSize + 1} to{' '}
          {Math.min((page + 1) * pageSize, data.total)} of {data.total} prospects
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * pageSize >= data.total}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
